import type { ProjectSnapshot } from "@codvero/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Archive, Download, History, Trash2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useConfirm } from "@/ui/ConfirmDialog.tsx";

import { projectsApi } from "@/api/projects.ts";
import { Alert, Button, Card, Input } from "@/ui/primitives.tsx";
import { useApiError } from "@/ui/useApiError.ts";

function fmtBytes(b: number) {
  return b >= 1 << 30 ? `${(b / (1 << 30)).toFixed(2)} GB` : b >= 1 << 20 ? `${(b / (1 << 20)).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`;
}

/** Encrypted workspace snapshots with restore/download, like the original's Settings > Snapshots. */
/** Outside the component: one created during render is a new type each time and remounts its children (inputs lose focus). */
const BareBox = ({ className, children }: { className?: string; children: React.ReactNode }) => <div className={className}>{children}</div>;

export function SnapshotsCard({ projectId, canEdit, running, bare = false }: { projectId: string; canEdit: boolean; running: boolean; bare?: boolean }) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const toMsg = useApiError();
  const key = ["projects", projectId, "snapshots"];
  const list = useQuery({ queryKey: key, queryFn: () => projectsApi.snapshots(projectId) });
  const [label, setLabel] = useState("");
  const [confirm, setConfirm] = useState<ProjectSnapshot | null>(null);
  const refresh = () => void qc.invalidateQueries({ queryKey: key });
  const create = useMutation({ mutationFn: () => projectsApi.createSnapshot(projectId, label), onSuccess: () => { setLabel(""); refresh(); } });
  const restore = useMutation({ mutationFn: (sid: string) => projectsApi.restoreSnapshot(projectId, sid), onSuccess: () => { setConfirm(null); void qc.invalidateQueries({ queryKey: ["projects", projectId, "files"] }); } });
  const { confirm: ask } = useConfirm();
  const remove = useMutation({ mutationFn: (sid: string) => projectsApi.deleteSnapshot(projectId, sid), onSuccess: refresh });
  const askRemove = async (s: ProjectSnapshot) => { if (await ask({ title: t("confirm.deleteSnapshotTitle"), message: t("confirm.deleteSnapshotMsg", { label: s.label || new Date(s.createdAt).toLocaleString(i18n.language) }), confirmLabel: t("common.delete"), danger: true })) remove.mutate(s.id); };
  const err = create.error ?? restore.error ?? remove.error;
  const Wrap = bare ? BareBox : Card;
  return (
    <Wrap className="space-y-3">
      {!bare && <h3 className="flex items-center gap-2 font-semibold"><Archive className="size-4 text-fg-muted" /> {t("snapshots.title")}</h3>}
      {!bare && <p className="text-sm text-fg-muted">{t("snapshots.intro")}</p>}
      {err && <Alert tone="danger">{toMsg(err)}</Alert>}
      {canEdit && (
        <form className="flex flex-wrap gap-2" onSubmit={(e) => { e.preventDefault(); create.mutate(); }}>
          <Input className="max-w-xs" placeholder={t("snapshots.labelPlaceholder")} maxLength={80} value={label} onChange={(e) => setLabel(e.target.value)} />
          <Button type="submit" loading={create.isPending}>{t("snapshots.take")}</Button>
        </form>
      )}
      {restore.isSuccess && <Alert tone="success">{t("snapshots.restored")}</Alert>}
      {confirm && (
        <Alert tone="warn" className="space-y-2">
          <div>{t("snapshots.restoreConfirm", { label: confirm.label || new Date(confirm.createdAt).toLocaleString(i18n.language) })}</div>
          <div className="flex gap-2">
            <Button size="sm" variant="danger" loading={restore.isPending} disabled={!running} onClick={() => restore.mutate(confirm.id)}>{t("snapshots.restore")}</Button>
            <Button size="sm" variant="ghost" onClick={() => setConfirm(null)}>{t("common.cancel")}</Button>
            {!running && <span className="text-xs">{t("errors.project_not_running")}</span>}
          </div>
        </Alert>
      )}
      {list.data?.length === 0 && <p className="text-sm text-fg-muted">{t("snapshots.none")}</p>}
      <ul className="divide-y divide-border">
        {(list.data ?? []).map((s) => (
          <li key={s.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 py-2 text-sm">
            <div className="min-w-48 flex-1">
              <div className="font-medium">{s.label || t("snapshots.unlabeled")} {s.status !== "ready" && <span className="ms-1 rounded-full bg-surface-2 px-1.5 text-[10px]">{t(`snapshots.status.${s.status}`)}</span>}</div>
              <div className="text-[11px] text-fg-muted" dir="auto">{new Date(s.createdAt).toLocaleString(i18n.language)} · {s.createdByName} · {fmtBytes(s.sizeBytes)}{s.error ? ` · ${s.error}` : ""}</div>
            </div>
            {s.status === "ready" && (
              <div className="flex items-center gap-1">
                {canEdit && <Button size="sm" variant="secondary" onClick={() => setConfirm(s)} title={t("snapshots.restore")}><History className="size-3.5" /> {t("snapshots.restore")}</Button>}
                {canEdit && <a className="rounded p-1.5 text-fg-muted hover:text-fg" href={projectsApi.snapshotDownloadUrl(projectId, s.id)} title={t("snapshots.download")}><Download className="size-4" /></a>}
                {canEdit && <button type="button" className="rounded p-1.5 text-fg-muted hover:text-danger" title={t("common.delete")} onClick={() => void askRemove(s)}><Trash2 className="size-4" /></button>}
              </div>
            )}
          </li>
        ))}
      </ul>
    </Wrap>
  );
}
